import Loader from '@/components/common/Loader/Loader';
import PrivateLayout from '@/layout/PrivateLayout';
import SignUp from '@/pages/authentication/signup';
import VerifyOtp from '@/pages/authentication/verifyOtp';
import PendingFeedback from '@/pages/feedback/PendingFeedback';
import Home from '@/pages/home';
import VideoCall from '@/pages/meeting';
import Feedback from '@/pages/meeting/Feedback';
import HelpUsImprove from '@/pages/meeting/HelpUsImprove';
import PaymentSummary from '@/pages/paymentSummary';
import PaymentDetails from '@/pages/paymentSummaryDetails';
import Profile from '@/pages/profile';
import EditProfile from '@/pages/profile/editProfile';
import ProfileCompletion from '@/pages/profile/profileCompletion';
import SessionDetails from '@/pages/sessionDetails';
import Sessions from '@/pages/sessions';
import Subscription from '@/pages/subscription';
import SuccessPage from '@/pages/subscription/components/SuccessPage';
import TrainerDetails from '@/pages/trainerDetails';
import TrainerList from '@/pages/trainerList';
import { Role } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useReactiveVar } from '@apollo/client';
import { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { getRoutes } from './routePaths';

const PublicLayout = lazy(() => import('@/layout/PublicLayout'));
const NotFound = lazy(() => import('@/pages/404'));
const Login = lazy(() => import('@/pages/authentication/login'));

const LoadingFallback = () => <Loader />;

interface PrivateRouteProps {
  children: React.ReactNode;
}

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo: string;
}

export const Navigator = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const redirectPath = searchParams.get('redirect');
  const { authenticated, userDetails } = useReactiveVar(authState);
  const isAuthenticated = useMemo(() => authenticated, [authenticated]);

  const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
    if (!isAuthenticated) {
      return <Navigate to={`${getRoutes.Login}${location.search}`} replace />;
    } else if (!userDetails?.role || !userDetails.firstName || !userDetails.email) {
      return <Navigate to={`${getRoutes.SignUp}${location.search}`} />;
    } else if (redirectPath) {
      return <Navigate to={redirectPath} />;
    } else {
      return children;
    }
  };

  const PublicRoute: React.FC<PublicRouteProps> = ({ children, redirectTo }) => {
    if (isAuthenticated) {
      if (redirectPath) {
        return <Navigate to={redirectPath} replace />;
      }
      return <Navigate to={redirectTo} replace />;
    }
    return children;
  };

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route
            path={getRoutes.Login}
            element={
              <PublicRoute redirectTo={`${getRoutes.home}${location.search}`}>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path={getRoutes.SignUp}
            element={
              isAuthenticated ? (
                userDetails?.role === null || !userDetails?.firstName || !userDetails.email ? (
                  <SignUp />
                ) : userDetails?.role !== Role.Trainee ? (
                  <Navigate to={getRoutes.Sessions} replace />
                ) : (
                  <Navigate to={`${getRoutes.home}${location.search}`} replace />
                )
              ) : (
                <Navigate to={`${getRoutes.Login}${location.search}`} replace />
              )
            }
          />
          <Route
            path={getRoutes.VerifyOtp}
            element={
              <PublicRoute redirectTo={`${getRoutes.home}${location.search}`}>
                <VerifyOtp />
              </PublicRoute>
            }
          />
          <Route
            path={getRoutes.Trainer}
            element={
              isAuthenticated ? (
                userDetails?.role === null ? (
                  <Navigate to={`${getRoutes.SignUp}${location.search}`} replace />
                ) : userDetails?.role !== Role.Trainee ? (
                  <Navigate to={getRoutes.Sessions} replace />
                ) : (
                  <Navigate to={`${getRoutes.home}${location.search}`} replace />
                )
              ) : (
                <Navigate to={`${getRoutes.Login}${location.search}`} replace />
              )
            }
          />
          <Route path={getRoutes.NotFound} element={<NotFound />} />
        </Route>
        <Route element={<PrivateLayout />}>
          <Route
            path={getRoutes.home}
            element={
              <PrivateRoute>
                {userDetails?.role === Role.Trainee ? (
                  <Home />
                ) : (
                  <Navigate to={getRoutes.Sessions} replace />
                )}
              </PrivateRoute>
            }
          />
          <Route
            path={getRoutes.Sessions}
            element={
              <PrivateRoute>
                <Sessions />
              </PrivateRoute>
            }
          />
          <Route
            path={getRoutes.Payment}
            element={
              <PrivateRoute>
                <PaymentSummary />
              </PrivateRoute>
            }
          />

          <Route
            path={getRoutes.Subscription}
            element={
              <PrivateRoute>
                <Subscription />
              </PrivateRoute>
            }
          />
          <Route
            path={getRoutes.Profile}
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path={getRoutes.PrivateNotFound}
            element={
              <PrivateRoute>
                <NotFound />
              </PrivateRoute>
            }
          />
          <Route
            path={getRoutes.ProfileCompletion}
            element={
              <PrivateRoute>
                <ProfileCompletion />
              </PrivateRoute>
            }
          />

          <Route
            path={getRoutes.SessionDetails}
            element={
              <PrivateRoute>
                <SessionDetails />
              </PrivateRoute>
            }
          />
          <Route
            path={getRoutes.PaymentDetails}
            element={
              <PrivateRoute>
                <PaymentDetails />
              </PrivateRoute>
            }
          />
        </Route>
        <Route
          path={getRoutes.EditProfile}
          element={
            <PrivateRoute>
              <EditProfile />
            </PrivateRoute>
          }
        />
        <Route
          path={getRoutes.Trainers}
          element={
            <PrivateRoute>
              <TrainerList />
            </PrivateRoute>
          }
        />

        <Route
          path={getRoutes.TrainerDetails}
          element={
            <PrivateRoute>
              <TrainerDetails />
            </PrivateRoute>
          }
        />
        <Route
          path={getRoutes.SubscriptionSuccess}
          element={
            <PrivateRoute>
              <SuccessPage />
            </PrivateRoute>
          }
        />
        <Route
          path={getRoutes.Meeting}
          element={
            <PrivateRoute>
              <VideoCall />
            </PrivateRoute>
          }
        />
        <Route
          path={getRoutes.Feedback}
          element={
            <PrivateRoute>
              <Feedback />
            </PrivateRoute>
          }
        />
        <Route
          path={getRoutes.FeedbackPending}
          element={
            <PrivateRoute>
              <PendingFeedback />
            </PrivateRoute>
          }
        />
        <Route
          path={getRoutes.HelpUsImprove}
          element={
            <PrivateRoute>
              <HelpUsImprove />
            </PrivateRoute>
          }
        />


        <Route
          path='/'
          element={
            isAuthenticated ? (
              userDetails?.role === Role.Trainee ? (
                <Navigate to={`${getRoutes.home}${location.search}`} replace />
              ) : (
                <Navigate to={getRoutes.Sessions} replace />
              )
            ) : (
              <Navigate to={`${getRoutes.Login}${location.search}`} replace />
            )
          }
        />
        <Route
          path='*'
          element={
            isAuthenticated ? (
              <Navigate to={getRoutes.PrivateNotFound} replace />
            ) : (
              <Navigate to={getRoutes.NotFound} replace />
            )
          }
        />
      </Routes>
    </Suspense>
  );
};
